import { getActiveLocale } from "../i18n";
import { API_BASE_URL } from "./apiClient";
import { APP_VERSION, CLIENT_PLATFORM, DEFAULT_DEVICE_LABEL } from "./clientMeta";
import { createQueuedTransport } from "./queuedTransport";

type Severity = "debug" | "info" | "warn" | "error" | "fatal";

type ReporterContext = {
  userId?: string | null;
  workspaceId?: string | null;
  sessionId?: string | null;
  requestId?: string | null;
  userState?: "anonymous" | "authenticated" | "admin" | "support";
  locale?: string | null;
};

type Breadcrumb = {
  message: string;
  category?: string;
  level?: Severity;
  timestamp?: number;
};

type ClientErrorInput = {
  message?: string;
  name?: string;
  stack?: string;
  severity?: Severity;
  fingerprint?: string;
  route?: string;
  url?: string;
  tags?: Record<string, unknown>;
  context?: Record<string, unknown>;
  breadcrumbs?: Breadcrumb[];
  occurredAt?: string;
  userState?: ReporterContext["userState"];
  sessionId?: string | null;
  requestId?: string;
  workspaceId?: string;
  userId?: string;
  device?: string;
};

type ClientErrorPayload = {
  id: string;
  occurred_at: string;
  platform: string;
  release: string;
  locale: string;
  route?: string;
  url?: string;
  message: string;
  name?: string;
  stack?: string;
  fingerprint?: string;
  severity: Severity;
  user_state?: string;
  session_id?: string;
  request_id?: string;
  workspace_id?: string;
  user_id?: string;
  device?: string;
  tags?: Record<string, unknown>;
  context?: Record<string, unknown>;
};

const STORAGE_KEY = "lotlocator:client-error-queue";
const MAX_BATCH_SIZE = 10;
const FLUSH_INTERVAL_MS = 2000;
const MAX_MESSAGE_LEN = 1800;
const MAX_STACK_LEN = 7000;
const MAX_TEXT_LEN = 512;
const MAX_URL_LEN = 1800;
const MAX_JSON_BYTES = 12000;
const RECENT_TTL_MS = 60_000;

const reporterLog = {
  warn: (...args: unknown[]) => {
    console.warn("[clientErrorReporter]", ...args);
  }
};

const transportLogger = {
  debug: (...args: unknown[]) => reporterLog.warn(...args),
  info: (...args: unknown[]) => reporterLog.warn(...args),
  warn: (...args: unknown[]) => reporterLog.warn(...args),
  error: (...args: unknown[]) => reporterLog.warn(...args),
  level: "warn" as const
};
const recentFingerprints = new Map<string, number>();
let reporterContext: ReporterContext = {};
let reporterInitialized = false;

const transport = createQueuedTransport<ClientErrorPayload>({
  storageKey: STORAGE_KEY,
  maxBatchSize: MAX_BATCH_SIZE,
  flushIntervalMs: FLUSH_INTERVAL_MS,
  logger: transportLogger,
  postBatch: (errors, auth) => postErrors(errors, auth.accessToken ?? null)
});

export function setClientErrorContext(context: ReporterContext) {
  reporterContext = sanitizeContext({
    ...reporterContext,
    ...context
  });
}

export function setClientErrorAuthToken(token: string | null) {
  transport.setAuthToken(token ?? null);
}

export function initializeClientErrorReporter() {
  if (reporterInitialized || typeof window === "undefined") {
    return;
  }
  reporterInitialized = true;

  window.addEventListener("error", (event) => {
    const { message, filename, lineno, colno } = event;
    const error = event.error ?? message ?? "Unknown client error";
    reportClientError(error, {
      severity: "fatal",
      route: window.location?.pathname,
      url: window.location?.href,
      context: {
        filename,
        line: lineno,
        column: colno
      }
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = (event as PromiseRejectionEvent).reason ?? "Unhandled rejection";
    reportClientError(reason, {
      severity: "error",
      route: window.location?.pathname,
      url: window.location?.href,
      context: {
        reasonType: typeof reason,
        message: typeof reason === "object" && reason !== null && "message" in reason ? (reason as { message?: string }).message : undefined
      }
    });
  });
}

export function reportClientError(error: unknown, input: ClientErrorInput = {}) {
  try {
    const normalized = normalizeError(error, input);
    const fingerprint = clampText(
      input.fingerprint ?? normalized.fingerprint ?? deriveFingerprint(normalized.message, normalized.stack, input.route),
      MAX_TEXT_LEN
    );

    if (fingerprint && shouldThrottle(fingerprint)) {
      return;
    }

    const payload = buildPayload({ ...input, ...normalized, fingerprint });
    transport.enqueue(payload);
  } catch (err) {
    reporterLog.warn("Unable to report client error", err);
  }
}

async function postErrors(errors: ClientErrorPayload[], accessToken: string | null) {
  if (errors.length === 0) return;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "Accept-Language": getActiveLocale()
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const res = await fetch(`${API_BASE_URL}/client-logs/errors`, {
    method: "POST",
    body: JSON.stringify({ errors }),
    headers,
    credentials: "include",
    keepalive: true
  });

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `client error ingestion failed with ${res.status}`);
  }
}

function buildPayload(input: ClientErrorInput & { message: string; fingerprint?: string }): ClientErrorPayload {
  const occurredAt = input.occurredAt ?? new Date().toISOString();
  const contextState = sanitizeContext(reporterContext);
  const sessionId = coalesce(input.sessionId, contextState.sessionId) ?? undefined;
  const userState = input.userState ?? contextState.userState ?? "anonymous";
  const workspaceId = coalesce(input.workspaceId, contextState.workspaceId) ?? undefined;
  const userId = coalesce(input.userId, contextState.userId) ?? undefined;
  const requestId = input.requestId ?? contextState.requestId ?? undefined;
  const locale = (contextState.locale ?? getActiveLocale() ?? "").trim() || getActiveLocale();

  const route = clampText(input.route ?? (typeof window !== "undefined" ? window.location?.pathname : ""), MAX_TEXT_LEN);
  const url = clampText(input.url ?? (typeof window !== "undefined" ? window.location?.href : ""), MAX_URL_LEN);
  const message = clampText(input.message, MAX_MESSAGE_LEN) || "Unknown error";
  const stack = input.stack ? clampText(input.stack, MAX_STACK_LEN) : undefined;
  const severity = normalizeSeverity(input.severity);

  const context = boundJson(
    {
      ...(input.context ?? {}),
      network: getNetworkInfo(),
      breadcrumbs: (input.breadcrumbs ?? []).slice(-20)
    },
    "context"
  );

  const tags = boundJson(input.tags ?? {}, "tags");

  return {
    id: safeRandomUUID(),
    occurred_at: occurredAt,
    platform: CLIENT_PLATFORM,
    release: APP_VERSION,
    locale,
    route: route || undefined,
    url: url || undefined,
    message,
    name: input.name ? clampText(input.name, MAX_TEXT_LEN) : undefined,
    stack,
    fingerprint: input.fingerprint,
    severity,
    user_state: userState,
    session_id: sessionId ?? undefined,
    request_id: requestId,
    workspace_id: workspaceId,
    user_id: userId,
    device: clampText(input.device ?? DEFAULT_DEVICE_LABEL, MAX_TEXT_LEN),
    tags,
    context
  };
}

function normalizeError(error: unknown, input: ClientErrorInput): { message: string; name?: string; stack?: string; fingerprint?: string } {
  if (error instanceof Error) {
    return {
      message: error.message || input.message || "Unknown error",
      name: error.name || input.name,
      stack: error.stack || input.stack
    };
  }

  if (typeof error === "string") {
    return { message: error || input.message || "Unknown error", stack: input.stack, name: input.name };
  }

  if (typeof error === "object" && error !== null) {
    const maybeError = error as { message?: string; name?: string; stack?: string };
    return {
      message: maybeError.message || input.message || "Unknown error",
      name: maybeError.name || input.name,
      stack: maybeError.stack || input.stack
    };
  }

  return { message: input.message || "Unknown error", name: input.name, stack: input.stack };
}

function normalizeSeverity(severity?: Severity) {
  if (!severity) return "error";
  const normalized = severity.toLowerCase() as Severity;
  if (normalized === "warn" || normalized === "error" || normalized === "fatal" || normalized === "info" || normalized === "debug") {
    return normalized;
  }
  return "error";
}

function deriveFingerprint(message: string, stack?: string, route?: string) {
  const basis = `${message}|${stack?.slice(0, 120) ?? ""}|${route ?? ""}`;
  let hash = 0;
  for (let i = 0; i < basis.length; i += 1) {
    hash = (hash << 5) - hash + basis.charCodeAt(i);
    hash |= 0; // force 32-bit
  }
  return `fe_${Math.abs(hash)}`;
}

function shouldThrottle(fingerprint?: string) {
  if (!fingerprint) return false;
  const now = Date.now();
  const last = recentFingerprints.get(fingerprint);
  recentFingerprints.set(fingerprint, now);
  if (recentFingerprints.size > 500) {
    for (const [key, ts] of recentFingerprints.entries()) {
      if (now - ts > RECENT_TTL_MS) {
        recentFingerprints.delete(key);
      }
    }
  }
  return typeof last === "number" && now - last < RECENT_TTL_MS;
}

function getNetworkInfo() {
  if (typeof navigator === "undefined") {
    return undefined;
  }
  const connection = (navigator as unknown as { connection?: { effectiveType?: string; downlink?: number; rtt?: number } }).connection;
  return {
    online: navigator.onLine,
    effectiveType: connection?.effectiveType,
    downlink: connection?.downlink,
    rtt: connection?.rtt
  };
}

function sanitizeContext(context: ReporterContext): ReporterContext {
	return Object.fromEntries(
    Object.entries(context).filter(([, value]) => value !== undefined && value !== null)
  ) as ReporterContext;
}

function clampText(value: string, max: number) {
  if (!value) return "";
  const trimmed = value.trim();
  const runes = Array.from(trimmed);
  if (runes.length <= max) {
    return trimmed;
  }
  return runes.slice(0, max).join("");
}

function safeRandomUUID(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function coalesce<T>(...values: (T | undefined | null)[]): T | undefined {
  for (const value of values) {
    if (value !== undefined && value !== null && `${value}` !== "") {
      return value as T;
    }
  }
  return undefined;
}

function boundJson(value: Record<string, unknown> | undefined, field: string) {
  if (!value) return undefined;
  try {
    const json = JSON.stringify(value);
    if (json.length > MAX_JSON_BYTES) {
      reporterLog.warn(`Truncating ${field} payload`, { bytes: json.length });
      return { truncated: true };
    }
    return value;
  } catch (error) {
    reporterLog.warn(`Dropping invalid ${field} payload`, error);
    return undefined;
  }
}
